const post = {
  slug: 'prompt-engineering-is-api-design',
  number: 11,
  title: 'Prompt Engineering Is Just API Design in Disguise',
  date: '2025-11-14',
  readTime: '6 min read',
  views: 41,
  category: 'ai',
  tags: ['AI/ML', 'LLM', 'Prompting'],
  excerpt:
    'The phrase prompt engineering makes it sound mystical. Most of what works is the same discipline you already use to design a good function or a clean API.',
  cover: null,
  content: `
The phrase prompt engineering makes it sound like a dark art, something you learn from cryptic threads and lucky incantations. It is not. The day I stopped treating prompts as spells and started treating them as interfaces, they got dramatically more reliable, and the mystery evaporated. Most of what actually works is the same discipline you already use to design a good function or a clean API.

If you can design a sensible interface, you can write a good prompt. It is the same skill pointed at a new kind of caller.

## A prompt is a function signature

When you design a function, you are clear about what goes in and what comes out. A prompt is that same contract written in plain language. The clearer you are about the inputs, the constraints, and the exact shape of the output, the more predictable the result.

\`\`\`text
You are a classifier.
Input:  a customer message.
Output: exactly one of [billing, technical, account, other].
Return only the label, nothing else.
\`\`\`

That is a function signature. Vague in, vague out. Specific in, specific out. The model is not reading your mind any more than a function reads its caller's. It works with what you actually pass it, so pass it something precise.

## Constrain the output, always

The biggest source of pain with model output is that it is too free. It adds a friendly preamble, wraps the answer in prose, or returns a slightly different format each time, and your downstream code, which expected something tidy, breaks.

The fix is to pin the output down the way you would define a return type. Tell it exactly what to return and nothing else. Ask for a strict structure. When the shape matters, give one concrete example of the exact output you expect, because a single example does more than a paragraph of description.

\`\`\`text
Return JSON only, no prose:
  { "label": "billing", "confidence": 0.0-1.0 }
\`\`\`

A model that returns clean, predictable output is a model your code can actually depend on. Everything else is you cleaning up after a vague contract.

## Give it the types it needs

A good function does not make the caller guess what a parameter means. A good prompt does the same by spelling out its inputs. If a field can be empty, say so. If there is a default behaviour when information is missing, state it. If a value has a specific meaning, define it.

The model handles ambiguity the only way it can, by guessing, and you remove the guessing by being explicit, exactly as you would with a well-typed interface. Most of the "the model is unreliable" complaints I have looked into were really "the prompt left something undefined and the model filled the gap."

## Handle the error cases

This is the step almost everyone skips, and it is where the worst failures live. Real input is messy. What should the model do when the question is off-topic, when the provided context does not contain an answer, when the input is garbage?

If you do not define that, the model will improvise, and it tends to improvise confidently. So design the failure path on purpose, the same way you write input validation.

\`\`\`text
If the context does not contain the answer, reply exactly:
  "I do not have that information."
Do not guess. Do not fill in plausible details.
\`\`\`

That one instruction prevents an entire category of confident, wrong answers, the same way a guard clause prevents a whole class of bugs.

## The reframe, and a short checklist

Thinking of prompts as API design takes the magic out of it, and that is the point. You stop hoping the model understands you and start specifying what you want. Before I consider a prompt done, I check:

- Is the task stated as clearly as a function signature?
- Is the output format pinned down, ideally with one example?
- Are the inputs and their meanings explicit, including empty and default cases?
- Is there a defined behaviour for off-topic, missing, or junk input?

None of that is a new skill you have to learn from scratch. It is the skill you already have as an engineer, applied to a caller that happens to speak English. Treat the model like an API and it starts behaving like one.
`,
};

export default post;
